import type { Theme } from '@material-ui/core/styles'
import { useMediaQuery as muiUseMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/styles'
import { useFrame } from 'react-frame-component'

export function useMediaQuery(
  mediaQuery: number | string | ((theme: Theme) => string) | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'print',
  options?: { up?: boolean; noSsr?: boolean }
) {
  const noSsr = options?.noSsr ?? true
  const up = options?.up ?? false

  // when rendered inside a react-frame-component iframe (e.g. the presentation
  // previews), queries must be evaluated against the iframe's viewport, not the
  // parent window's. Outside a frame, useFrame returns the top window.
  const { window: win } = useFrame()
  const muiOptions = {
    noSsr,
    ...(win?.matchMedia && { matchMedia: win.matchMedia.bind(win) })
  }

  const theme = useTheme<Theme>()
  const isPrint = muiUseMediaQuery('print', muiOptions)

  // If a function is passed (e.g. (theme) => theme.breakpoints.down('sm')),
  // resolve it and use directly with MUI's useMediaQuery
  if (typeof mediaQuery === 'function') {
    const resolved = mediaQuery(theme)
    return muiUseMediaQuery(resolved, muiOptions)
  }

  // If a raw CSS media query string is passed (e.g. from theme.breakpoints.down()),
  // use it directly with MUI's useMediaQuery
  if (typeof mediaQuery === 'string' && mediaQuery.startsWith('@media')) {
    return muiUseMediaQuery(mediaQuery, muiOptions)
  }

  const breakpointName = mediaQuery === 'print' ? 'lg' : mediaQuery

  const breakpoint = up ? theme.breakpoints.up(breakpointName as any) : theme.breakpoints.down(breakpointName as any)
  const queryResult = muiUseMediaQuery(breakpoint, muiOptions)

  if (mediaQuery === 'print') {
    return isPrint
  }

  return !isPrint && queryResult
}
